import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { BRAND } from '../config/brand';
import api from '../config/api';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OAuth onboarding (new Yandex user)
  const [showYandexOnboarding, setShowYandexOnboarding] = useState(false);
  const [onboardingBusy, setOnboardingBusy] = useState(false);

  // Handle OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const oauth = searchParams.get('oauth');
    const isNew = searchParams.get('new');
    const error = searchParams.get('error');
    const reason = searchParams.get('reason');

    if (error === 'oauth_failed') {
      const errorMessage = reason ? `Ошибка входа через Yandex: ${reason}` : 'Ошибка входа через Yandex';
      toast.error(errorMessage);
      return;
    }

    if (token && oauth === 'yandex') {
      // Store the token
      localStorage.setItem('token', token);

      // If this was a NEW user created via Yandex, show onboarding modal first.
      if (String(isNew) === '1') {
        toast.success('Аккаунт создан через Яндекс!');
        setShowYandexOnboarding(true);
        return;
      }

      toast.success('Добро пожаловать через Яндекс!');
      // Reload the page to refresh the auth state
      window.location.href = '/';
    }
  }, [searchParams, navigate]);

  const finishYandexOnboarding = async (type) => {
    if (onboardingBusy) return;
    setOnboardingBusy(true);

    try {
      if (type === 'profile') {
        await api.post('/api/account/account-type', { account_type: 'profile' });
      }

      // Apply choice
      setShowYandexOnboarding(false);

      if (type === 'profile') {
        // go to settings to complete profile
        window.location.href = '/settings';
      } else {
        // guest -> home
        window.location.href = '/';
      }
    } catch (e) {
      console.error('Yandex onboarding error:', e);
      toast.error('Не удалось сохранить выбор. Попробуйте ещё раз.');
    } finally {
      setOnboardingBusy(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Добро пожаловать!');
      // Redirect back if provided (e.g. from ProtectedRoute / тарифы)
      const next = searchParams.get('next');
      if (next) {
        navigate(decodeURIComponent(next), { replace: true });
        return;
      }
      // Fallback: go home
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Неверный email или пароль');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {showYandexOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-secondary-900">Создание аккаунта</h3>
            <p className="mt-2 text-secondary-700">
              Вы хотите создать профиль или продолжить как гость?
            </p>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                disabled={onboardingBusy}
                onClick={() => finishYandexOnboarding('profile')}
                className="btn btn-primary w-full"
              >
                {onboardingBusy ? 'Сохранение...' : 'Создать профиль'}
              </button>

              <button
                type="button"
                disabled={onboardingBusy}
                onClick={() => finishYandexOnboarding('guest')}
                className="btn btn-secondary w-full"
              >
                Продолжить как гость
              </button>
            </div>

            <p className="mt-4 text-xs text-secondary-500">
              Профиль можно создать позже в настройках.
            </p>
          </div>
        </div>
      )}
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">a</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900">
            Войти в аккаунт
          </h2>
          <p className="mt-2 text-secondary-600">
            Или{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
              создайте новый аккаунт
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  {...register('email', {
                    required: 'Email обязателен',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Неверный формат email'
                    }
                  })}
                  className="input pl-10"
                  placeholder="your@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-orange-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                Пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Пароль обязателен',
                    minLength: {
                      value: 6,
                      message: 'Пароль должен содержать минимум 6 символов'
                    }
                  })}
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-orange-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-secondary-700">
                Запомнить меня
              </label>
            </div>

            <div className="text-sm">
              <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500 font-medium">
                Забыли пароль?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Вход...
              </>
            ) : (
              'Войти'
            )}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondary-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-secondary-50 text-secondary-500">Или войдите через</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || BRAND.baseUrl}/api/auth/yandex`}
              className="btn btn-secondary w-full flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.4 2.096C10.746 2.096 8.6 4.242 8.6 6.896c0 1.087.354 2.087.95 2.9L2.096 13.4C.95 13.4 0 14.35 0 15.5s.95 2.1 2.096 2.1c1.146 0 2.096-.95 2.096-2.1 0-.354-.087-.687-.242-.996l7.454-3.604c.354.596.95.996 1.596.996.55 0 1-.45 1-1s-.45-1-1-1c-.354 0-.687.2-.85.5L7.2 8.896C7.2 8.896 7.2 8.896 7.2 8.896c-.146-.3-.242-.65-.242-1 0-1.65 1.35-3 3-3s3 1.35 3 3c0 .35-.096.7-.242 1l-3.604 7.454c.596.354.996.95.996 1.596 0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2c-.354 0-.687.2-.85.5L3.604-7.454c.3-.146.5-.5.5-.85 0-.55-.45-1-1-1s-1 .45-1 1c0 .354.2.687.5.85l7.454 3.604c-.354.596-.95.996-1.596.996-1.65 0-3-1.35-3-3z" />
              </svg>
              <span>Yandex</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
